import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetch_vital_graphs, fetch_vital_graph } from '../../../../services/rpm/Service';
import { VITAL_KEYWORDS, VITAL_RANGES } from '../../../../constants';
import { error } from 'jquery';
export const VitalsContext = createContext();


// create a custom hook to use the VitalsContext
export const useVitals = () => {
    const context = useContext(VitalsContext);
    if (!context) {
        throw new Error('useVitals must be used within a VitalsProvider');
    }
    return context;
};

// create a provider to wrap the app with the VitalsContext
export const VitalsProvider = ({ children }) => {
    const [memberName, setMemberName] = useState("");
    const [memberInfo, setMemberInfo] = useState({}); 
    const [isLoading, setIsLoading] = useState(false);
    const [vitals, setVitals] = useState([]); 
    const [ranges, setRanges] = useState();
    const [vitalStatsInfo, setVitalStatsInfo] = useState({}); 
    const [vitalStatsRanges, setVitalStatsRanges] = useState({}); 
    const [vitalsData, setVitalsData] = useState({
        heartRate: {
            current: "",
            currentObservation: "",
            history: []
        },
        bodyTemperature: {
            current: "",
            currentObservation: "",
            history: []
        },
        spO2: {
            current: "",
            currentObservation: "",
            history: []
        },
        bmi: {
            current: "",
            currentObservation: "",
        },
        bloodGlucose: {
            current: "",
            currentObservation: "",
            type: "Fasting",    
            history: []
        },
        bloodPressure: {
            current: "",
            currentObservation: "",
            history: []
        }
    });

    const [memberId, setMemberId] = useState(null);

    useEffect(() => { 
        Object.values(vitals).forEach(async (vital) => {            
            fetchSingleVitalGraph(vital.metricsId, vital.keyword);
        });
    }, [vitals]); 


    // fetch the vital graphs data
    function fetchVitalGraphsData(memberId) {
        setIsLoading(true);
        fetch_vital_graphs({
            userId: memberId
        }).then(response => {
            if (response.data.code === 200 && response.data.data) {
                const apiData = response?.data?.data?.data;
                setVitals(response?.data?.data?.metricsData); 


                setMemberName(response?.data?.data?.memberDetails?.memberName);
                setMemberInfo(response?.data?.data?.memberDetails);
            }
        }).catch(error => {
            console.error('Error fetching vital graphs data:', error);
        }).finally(() => {
            setIsLoading(false);
        });
    }


    // for fetching single vital graph
    function fetchSingleVitalGraph(vitalId, keyword){
        let newVitalsData = {}; 
        setIsLoading(true); 
        fetch_vital_graph({userId: memberId, metricsId: vitalId}).then((response) => {
            if(response.data.code === 200){
                const graphData = response.data?.data?.data; 
                const title = response.data?.data?.metricsName; 
                const unit = response.data?.data?.unit; 
                
                const lastIndex = graphData.length - 1; 

                // Initialize the vital type in newVitalsData
                if(keyword == "bp"){ 
                    newVitalsData = {
                        current: graphData.length > 0 ? graphData[lastIndex].paramVal.toString(): 0,
                        currentObservation: graphData.length > 0 ? graphData[lastIndex]?.observation.observation || "": "",
                        history: graphData.map(item => {
                            const [systolic, diastolic] = item.paramVal.split('/');
                            return {
                                date: item.createdTime,
                                systolic: parseInt(systolic),
                                diastolic: parseInt(diastolic)
                            };
                        })
                    };


                    if(graphData.length > 0){ 
                        setVitalStatsInfo((prev) => { 
                            return { 
                                ...prev, 
                                [keyword]: { 
                                    title: title,
                                    unit: unit,
                                    current: graphData.length > 0 ? graphData[lastIndex].paramVal.toString(): 0,
                                    currentObservation: graphData.length > 0 ? graphData[lastIndex]?.observation.observation || "": "",
                                    history:graphData.map(item => {
                                        const [systolic, diastolic] = item.paramVal.split('/');
                                        return {
                                            date: item.createdTime,
                                            systolic: parseInt(systolic),
                                            diastolic: parseInt(diastolic)
                                        };
                                    })
                                }
                            }
                        });
                    }
                }else { 
                    newVitalsData = {
                        fetched: true,
                        current: graphData.length > 0 ? graphData[lastIndex].paramVal.toString(): 0,
                        currentObservation: graphData.length > 0 ? graphData[lastIndex]?.observation.observation || "": "",
                        history: graphData.map(item => ({
                            date: item.createdTime,
                            value: parseFloat(item.paramVal)
                        }))
                    };


                    if(graphData.length > 0){ 
                        setVitalStatsInfo((prev) => { 
                            return { 
                                ...prev, 
                                [keyword]: { 
                                    title: title,
                                    unit: unit,
                                    current: graphData.length > 0 ? graphData[lastIndex].paramVal.toString(): 0,
                                    currentObservation: graphData.length > 0 ? graphData[lastIndex]?.observation.observation || "": "",
                                    history: graphData.map(item => ({
                                        date: item.createdTime,
                                        value: parseFloat(item.paramVal)
                                    }))
                                }
                            }
                        }); 
                    }
                }


                let vitalType = ""; 

                switch(keyword){ 
                    case "spO2": 
                        vitalType = "spO2"; 
                        break; 

                    case "pr": 
                        vitalType= "heartRate"; 
                        break; 
                    case "bt": 
                        vitalType="bodyTemperature"; 
                        break; 
                    case "bmi": 
                        vitalType= "bmi"; 
                        break; 
                    
                    case "bglf": 
                        vitalType="bloodGlucose"; 
                        break; 
                    
                    case "bgpp": 
                        vitalType= "bloodGlucoseRandom"; 
                        break; 
                    
                    case "bp": 
                        vitalType="bloodPressure"; 
                        break;
                    
                    default: 
                        vitalType="";
                        break; 
                }; 

                // Ensure that the vitalType is valid before updating
                if (vitalType) {
                    setVitalsData((prev) => { 
                        return { 
                            ...prev, 
                            [vitalType]: newVitalsData
                        }
                    })
                }

                
                // set the ranges as well 
                let jsondata = JSON.parse(response.data?.data?.ranges || '[]');
                let tempArr = []; 
                if(keyword === "bp") return; 

                jsondata.forEach(element => {
                    let bandData = {};
                    bandData['value'] = element.max === "" ? VITAL_RANGES.bglf.max : element.max;
                    bandData['zIndex'] = 5; 

                    bandData['color'] = element.color;
                    bandData['width'] =  2; 
                    bandData['dashStyle'] =  'ShortDot';
                    
                    bandData['label'] = {
                        text: element.observation,
                        color: element.color,
                        align: 'left', 
                        y: 10,
                        verticalAlign: 'middle',
                        style: {
                            color: 'black',
                        }
                    }

                    tempArr.push(bandData);
                }); 

                

                // setting the ranges here
                setRanges((prev) => { 
                    return { 
                        ...prev, 
                        [vitalType]: tempArr  || []
                    }
                }); 

                setVitalStatsRanges((prev)  => { 
                    return { 
                        ...prev, 
                        [keyword]: tempArr || []
                    }
                })
            }
                
                
            
        }).catch((err) => { 
            // continue with error 
            console.log(err); 
        }).finally(() =>{ 
            setIsLoading(false); 
        }); 
    }

    useEffect(() => {
        if (memberId) {
            fetchVitalGraphsData(memberId);
        }
    }, [memberId]);

    const updateVitalsData = (vitalType, newData) => {
        setVitalsData(prev => ({
            ...prev,
            [vitalType]: {
                ...prev[vitalType],
                ...newData
            }
        }));
    };

    const value = {
        vitalsData,
        updateVitalsData,
        memberId,
        setMemberId,
        memberName,
        isLoading, 
        ranges, 
        vitals, 
        fetchSingleVitalGraph,
        memberInfo, 
        vitalStatsInfo, 
        vitalStatsRanges
    };

    return (
        <VitalsContext.Provider value={value}>
            {children}
        </VitalsContext.Provider>
    );
}; 